/* eslint-disable no-empty */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { FaVideo, FaPhoneAlt } from "react-icons/fa";
import { IMaskInput } from "react-imask";

import {
  addDays,
  addMinutes,
  format,
  isSameMonth,
  startOfMonth,
} from "date-fns";

import moment from "moment-timezone";
import { timezoneFormatter } from "../../../utils/staticData";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

import axios from "axios";
import "./BookAppointmentFormV3.scss";

const cleintUrlProd = "https://app.unclekam.com";
const cleintUrlStaging = "https://staging.unclekam.com";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const ANNUAL_INCOMES = [
  { id: 1, name: "$12,000 – $49,999" },
  { id: 2, name: "$50,000 – $99,999" },
  { id: 3, name: "$100,000 – $249,999" },
  { id: 4, name: "$250,000 – $499,999" },
  { id: 5, name: "$500,000+" },
];

const SLOT_MINUTES = 45;
const UPCOMING_DAYS_TO_SHOW = 3;

const PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;

/* Emoji map for known service names — matched by substring (case-insensitive) */
const SERVICE_EMOJIS = [
  { match: "individual",   emoji: "👫" },
  { match: "family",       emoji: "👫" },
  { match: "tax prep",     emoji: "👫" },
  { match: "tax saving",   emoji: "📊" },
  { match: "planning",     emoji: "📊" },
  { match: "strateg",      emoji: "📊" },
  { match: "accounting",   emoji: "📦" },
  { match: "bookkeeping",  emoji: "📦" },
  { match: "retainer",     emoji: "🤝" },
];

const getServiceEmoji = (name) => {
  const lower = (name || "").toLowerCase();
  for (const entry of SERVICE_EMOJIS) {
    if (lower.includes(entry.match)) return entry.emoji;
  }
  return "📋";
};

/* =========================
   TIMEZONE OPTIONS
   ========================= */

const buildUSTzOptions = () => {
  const top = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
  ];
  const us = moment.tz.zonesForCountry("US") || [];
  const unique = Array.from(new Set([...top, ...us]));
  return unique.map((tz) => ({ value: tz, label: timezoneFormatter(tz) }));
};

const selectedLabel = (tz) =>
  `${timezoneFormatter(tz)} (${dayjs().tz(tz).format("h:mma")})`;

/* =========================
   SLOT/DATE HELPERS
   ========================= */

const generateTicks = (start, end, stepMinutes = SLOT_MINUTES) => {
  const out = [];
  let cur = new Date(start);
  while (cur < end) {
    out.push(cur);
    cur = addMinutes(cur, stepMinutes);
  }
  return out;
};

const parseTimeHM = (str) => {
  if (!str) return null;
  const s = String(str).trim();

  let m = s.match(/^(\d{1,2}):(\d{2})\s*([ap]m)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toLowerCase();
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return { hour: h, minute: min };
  }

  m = s.match(/^(\d{1,2})\s*([ap]m)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const ap = m[2].toLowerCase();
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return { hour: h, minute: 0 };
  }

  m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };

  m = s.match(/^(\d{1,2})$/);
  if (m) return { hour: parseInt(m[1], 10), minute: 0 };

  return null;
};

const dateAtLocalTime = (jsDate, timeStr, providerTZ) => {
  if (!jsDate || !timeStr || !providerTZ) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: providerTZ });
  } catch {
    providerTZ = "UTC";
  }

  const parts = parseTimeHM(timeStr);
  if (!parts) return null;

  const y = jsDate.getFullYear();
  const mo = jsDate.getMonth() + 1;
  const d = jsDate.getDate();

  const baseLocal = dayjs.tz(
    `${String(y).padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(
      d
    ).padStart(2, "0")}T00:00`,
    providerTZ
  );
  if (!baseLocal.isValid()) return null;

  const dt = baseLocal.add(parts.hour, "hour").add(parts.minute, "minute");
  return dt.isValid() ? dt.utc().toDate() : null;
};

const buildSlotsForDate = (date, payload, selectedTZ) => {
  const providerTZ = payload?.settings?.timezone;
  const weekdayName = WEEKDAYS[date.getDay()];
  const dayRanges = payload?.availability?.time_ranges?.[weekdayName];
  if (!providerTZ || !Array.isArray(dayRanges) || dayRanges.length === 0)
    return [];

  const slots = [];
  for (const range of dayRanges) {
    if (!range?.from || !range?.to) continue;

    const startUtc = dateAtLocalTime(date, range.from, providerTZ);
    const endUtc = dateAtLocalTime(date, range.to, providerTZ);
    if (!startUtc || !endUtc || !(endUtc > startUtc)) continue;

    const ticks = generateTicks(startUtc, endUtc, SLOT_MINUTES);
    for (const utcDate of ticks) {
      const label = dayjs(utcDate).utc().tz(selectedTZ).format("h:mm A");
      slots.push({ utc: utcDate, iso: utcDate.toISOString(), label });
    }
  }
  return slots;
};

const buildUpcomingDaysWithSlots = (payload, selectedTZ, maxDays) => {
  if (!payload) return [];
  const out = [];
  const today = new Date();
  for (let i = 1; i <= 180 && out.length < maxDays; i++) {
    const d = addDays(today, i);
    const slots = buildSlotsForDate(d, payload, selectedTZ);
    if (slots.length) out.push({ date: d, slots });
  }
  return out;
};

/* =========================
   Step 1: Time Picker
   ========================= */

const StepTimePicker = ({
  hasPayload,
  noSchedule,
  taxProAvailability,
  proName,
  tzOptions,
  selectedTZ,
  setSelectedTZ,
  selectedSlotIso,
  setSelectedSlotIso,
  setSelectedDate,
  appointmentType,
  setAppointmentType,
  loading,
  scheduleLoading,
  status,
  message,
  onContinue,
}) => {
  const [dayPage, setDayPage] = useState(0);

  const days = useMemo(() => {
    if (!hasPayload) return [];
    return buildUpcomingDaysWithSlots(
      taxProAvailability,
      selectedTZ.value,
      UPCOMING_DAYS_TO_SHOW
    );
  }, [hasPayload, taxProAvailability, selectedTZ.value]);

  useEffect(() => {
    setDayPage(0);
  }, [days.length]);

  const currentDay = days[dayPage] || null;

  if (scheduleLoading) {
    return (
      <div className="uk-booking-v3 container py-3">
        <div className="text-center py-5">
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
          Loading schedule...
        </div>
      </div>
    );
  }

  if (noSchedule) {
    return (
      <div className="uk-booking-v3 container py-3">
        <div className="text-center py-5 text-muted">
          No schedule available
        </div>
      </div>
    );
  }

  return (
    <div className="uk-booking-v3 container py-3">
      {/* Status messages */}
      <div className="mt-1">
        {status === "success" && (
          <div className="alert alert-success py-2 mb-2">{message}</div>
        )}
        {status === "error" && (
          <div className="alert alert-danger py-2 mb-2">{message}</div>
        )}
      </div>

      {/* Pro name */}
      {proName && (
        <div className="text-center mb-2">
          <span className="badge bg-light text-dark border px-3 py-2">
            Tax Pro: <strong>{proName}</strong>
          </span>
        </div>
      )}

      {/* Day header with pagination arrows */}
      {currentDay && (
        <div className="uk-booking-v3__day-nav d-flex align-items-center justify-content-center gap-3 mb-2">
          <button
            type="button"
            className="btn btn-sm uk-booking-v3__arrow"
            disabled={dayPage === 0}
            onClick={() => {
              setDayPage((p) => Math.max(0, p - 1));
              setSelectedSlotIso(null);
            }}
            aria-label="Previous day"
          >
            &larr;
          </button>

          <div className="text-center">
            <div className="fw-bold text-dark fs-5">
              {format(currentDay.date, "EEEE")}
            </div>
            <div style={{ color: "#4B57E7" }} className="small">
              {format(currentDay.date, "MMMM d, yyyy")}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-sm uk-booking-v3__arrow"
            disabled={dayPage >= days.length - 1}
            onClick={() => {
              setDayPage((p) => Math.min(days.length - 1, p + 1));
              setSelectedSlotIso(null);
            }}
            aria-label="Next day"
          >
            &rarr;
          </button>
        </div>
      )}

      {/* Dot indicators */}
      {days.length > 1 && (
        <div className="d-flex justify-content-center gap-1 mb-3">
          {days.map((_, i) => (
            <span
              key={i}
              className={`uk-booking-v3__dot ${i === dayPage ? "active" : ""}`}
            />
          ))}
        </div>
      )}

      {/* Time slots grid */}
      {currentDay && (
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          {currentDay.slots.map((s) => {
            const active = selectedSlotIso === s.iso;
            return (
              <button
                key={s.iso}
                type="button"
                className={[
                  "btn",
                  active ? "uk-booking-v3__slot-btn-active" : "",
                  "uk-booking-v3__slot-btn",
                ].join(" ")}
                onClick={() => {
                  setSelectedDate(currentDay.date);
                  setSelectedSlotIso(s.iso);
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      )}

      {!days.length && !noSchedule && (
        <div className="text-muted text-center py-4">
          No available times right now.
        </div>
      )}

      {/* Timezone selector */}
      <div className="d-flex justify-content-center mt-3">
        <div className="uk-booking-v3__tz">
          <select
            className="form-select uk-booking-v3__tz-select"
            value={selectedTZ.value}
            onChange={(e) => {
              const next = tzOptions.find((o) => o.value === e.target.value);
              if (next) setSelectedTZ(next);
            }}
          >
            {tzOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {selectedLabel(o.value)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Appointment type toggle */}
      <div className="uk-appt-type-row d-flex justify-content-center gap-3 mt-3">
        <button
          type="button"
          className={`uk-appt-type-btn ${appointmentType === "video" ? "active" : ""}`}
          onClick={() => setAppointmentType("video")}
        >
          <FaVideo className="me-2" />
          Video call
        </button>
        <button
          type="button"
          className={`uk-appt-type-btn ${appointmentType === "phone" ? "active" : ""}`}
          onClick={() => setAppointmentType("phone")}
        >
          <FaPhoneAlt className="me-2" />
          Phone call
        </button>
      </div>

      {/* Continue CTA */}
      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-dark btn-lg uk-booking-v3__cta"
          disabled={!selectedSlotIso || loading}
          onClick={onContinue}
        >
          Continue &rarr;
        </button>
      </div>

      {!selectedSlotIso && (
        <div className="text-center text-muted mt-2 small">
          Pick a time to continue
        </div>
      )}
    </div>
  );
};

/* =========================
   Step 2: Your Details (contact info)
   ========================= */

const StepDetails = ({ formData, setField, errors, onBack, onContinue }) => {
  return (
    <div className="uk-booking-v3 container py-3">
      <h4 className="fw-bold text-dark mb-1">Your details</h4>
      <p className="text-muted small mb-4">We'll use this to confirm your appointment</p>

      <div className="d-flex flex-column gap-3">
        <div className="d-flex gap-3">
          <div className="flex-fill">
            <input
              type="text"
              className={`form-control uk-booking-v3__input ${errors.firstname ? "is-invalid" : ""}`}
              placeholder="First Name *"
              value={formData.firstname}
              onChange={(e) => setField("firstname", e.target.value)}
            />
          </div>
          <div className="flex-fill">
            <input
              type="text"
              className={`form-control uk-booking-v3__input ${errors.lastname ? "is-invalid" : ""}`}
              placeholder="Last Name *"
              value={formData.lastname}
              onChange={(e) => setField("lastname", e.target.value)}
            />
          </div>
        </div>

        <input
          type="email"
          className={`form-control uk-booking-v3__input ${errors.email ? "is-invalid" : ""}`}
          placeholder="Email Address *"
          value={formData.email}
          onChange={(e) => setField("email", e.target.value)}
        />

        <IMaskInput
          className={`form-control uk-booking-v3__input ${errors.phone ? "is-invalid" : ""}`}
          placeholder="Phone Number *"
          mask="(000) 000-0000"
          lazy={true}
          overwrite={true}
          value={formData.phone}
          onAccept={(v) => setField("phone", v)}
          type="tel"
          inputMode="tel"
        />

        <input
          type="text"
          className={`form-control uk-booking-v3__input ${errors.zip ? "is-invalid" : ""}`}
          placeholder="Zip Code *"
          value={formData.zip}
          onChange={(e) => {
            const filtered = e.target.value.replace(/\D/g, "").slice(0, 5);
            setField("zip", filtered);
          }}
          inputMode="numeric"
          maxLength={5}
        />
      </div>

      <hr className="my-4" style={{ borderColor: "#eee", opacity: 0.5 }} />

      <div className="d-flex justify-content-between align-items-center">
        <button type="button" className="btn btn-link text-decoration-none px-0 text-dark" onClick={onBack}>
          &larr; Back
        </button>
        <button type="button" className="btn btn-dark btn-lg uk-booking-v3__cta" onClick={onContinue}>
          Continue &rarr;
        </button>
      </div>
    </div>
  );
};

/* =========================
   Step 3: Services
   ========================= */

const StepServices = ({ services, servicesLoading, formData, setFormData, onBack, onContinue }) => {
  return (
    <div className="uk-booking-v3 container py-3">
      <h4 className="fw-bold text-dark mb-1">What can we help with?</h4>
      <p className="text-muted small mb-4">Select all that apply</p>

      {servicesLoading && <div className="text-muted mb-2">Loading services…</div>}
      {!servicesLoading && services.length === 0 && (
        <div className="text-muted mb-2">No services found.</div>
      )}

      <div className="d-flex flex-column gap-2">
        {services.map((s) => {
          const checked = formData.options.includes(String(s.id));
          return (
            <label
              key={s.id}
              className={`uk-booking-v3__option-card ${checked ? "active" : ""}`}
            >
              <span className="uk-booking-v3__option-text">
                <span className="me-2">{getServiceEmoji(s.name)}</span>
                {s.name}
              </span>
              <input
                type="checkbox"
                className="form-check-input"
                checked={checked}
                onChange={(e) => {
                  const id = String(s.id);
                  setFormData((prev) => {
                    const next = new Set(prev.options.map(String));
                    if (e.target.checked) next.add(id);
                    else next.delete(id);
                    return { ...prev, options: Array.from(next) };
                  });
                }}
              />
            </label>
          );
        })}
      </div>

      <hr className="my-4" style={{ borderColor: "#eee", opacity: 0.5 }} />

      <div className="d-flex justify-content-between align-items-center">
        <button type="button" className="btn btn-link text-decoration-none px-0 text-dark" onClick={onBack}>
          &larr; Back
        </button>
        <button
          type="button"
          className="btn btn-dark btn-lg uk-booking-v3__cta"
          disabled={formData.options.length === 0}
          onClick={onContinue}
        >
          Continue &rarr;
        </button>
      </div>
    </div>
  );
};

/* =========================
   Step 4: Income + Agreement + Confirm
   ========================= */

const StepIncome = ({ formData, setField, loading, submitError, onBack, onConfirm }) => {
  return (
    <div className="uk-booking-v3 container py-3">
      <h4 className="fw-bold text-dark mb-1">Annual income range</h4>
      <p className="text-muted small mb-4">Helps us match you with the right advisor</p>

      <div className="d-flex flex-column gap-2">
        {ANNUAL_INCOMES.map((inc) => {
          const checked = String(formData.income) === String(inc.id);
          return (
            <label
              key={inc.id}
              className={`uk-booking-v3__option-card ${checked ? "active" : ""}`}
            >
              <span className="uk-booking-v3__option-text">{inc.name}</span>
              <input
                type="radio"
                name="annual_income"
                className="form-check-input"
                checked={checked}
                value={inc.id}
                onChange={(e) => setField("income", e.target.value)}
              />
            </label>
          );
        })}
      </div>

      {/* Agreement */}
      <div className="mt-4">
        <label className="d-flex align-items-start gap-2" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            className="form-check-input mt-1 flex-shrink-0"
            checked={!!formData.agreementAccepted}
            onChange={(e) => setField("agreementAccepted", e.target.checked)}
          />
          <span className="small text-dark">
            I agree to the{" "}
            <a href="https://app.unclekam.com/user-agreement" target="_blank" rel="noreferrer" className="text-dark fw-semibold text-decoration-underline">
              Uncle Kam Terms of Service
            </a>{" "}
            and{" "}
            <a href="https://app.unclekam.com/privacy-policy" target="_blank" rel="noreferrer" className="text-dark fw-semibold text-decoration-underline">
              Privacy Policy
            </a>
            , and agree to receive emails and texts.
          </span>
        </label>
      </div>

      {!!submitError && (
        <div className="alert alert-danger mt-3" role="alert">
          {submitError}
        </div>
      )}

      <hr className="my-4" style={{ borderColor: "#eee", opacity: 0.5 }} />

      <div className="d-flex justify-content-between align-items-center">
        <button type="button" className="btn btn-link text-decoration-none px-0 text-dark" onClick={onBack}>
          &larr; Back
        </button>
        <button
          type="button"
          className="btn btn-success btn-lg uk-booking-v3__cta"
          disabled={!formData.income || !formData.agreementAccepted || loading}
          onClick={onConfirm}
        >
          {loading && (
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
          )}
          Confirm Appointment
        </button>
      </div>
    </div>
  );
};

/* =========================
   Main V3 Component
   ========================= */

const BookAppointmentFormV3 = ({ qa_mode, auto_login = true, proxyUrl, pro_id: propProId }) => {
  const tzOptions = useMemo(buildUSTzOptions, []);
  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const initialTz = useMemo(() => {
    const guess = moment.tz.guess();
    return guess && guess.startsWith("America/") ? guess : "America/Adak";
  }, []);

  const [selectedTZ, setSelectedTZ] = useState(() => {
    const opt =
      tzOptions.find((o) => o.value === initialTz) || {
        value: initialTz,
        label: timezoneFormatter(initialTz),
      };
    return opt;
  });

  const [monthCursor, setMonthCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(addDays(new Date(), 1));
  const [selectedSlotIso, setSelectedSlotIso] = useState(null);

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [formData, setFormData] = useState(() => ({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    zip: "",
    options: [],
    income: "",
    agreementAccepted: false,
  }));
  const [fieldErrors, setFieldErrors] = useState({});

  const [taxProAvailability, setTaxProAvailability] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [noSchedule, setNoSchedule] = useState(false);

  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const emailParam = params.get("email");

  // pro_id resolution: URL param overrides widget attribute
  const pro_id = params.get("pro_id") || propProId;

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appointmentType, setAppointmentType] = useState("video");
  const [userCheck, setUserCheck] = useState({});

  // Unified step: 1=time, 2=details, 3=services, 4=income/confirm
  const [step, setStep] = useState(1);

  const qa = useMemo(
    () => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")),
    [qa_mode, params]
  );
  const CLIENT_URL = qa ? cleintUrlStaging : cleintUrlProd;

  function convertTimeIntoSselectedTZ(timezoneAssign, targetTimeZone, date, formatStr) {
    const currentTimeWithTZ = moment.tz(
      moment(date).format("YYYY-MM-DDTHH:mm:ss"),
      timezoneAssign
    );
    return moment(currentTimeWithTZ).tz(targetTimeZone).format(formatStr || "YYYY-MM-DD HH:mm:ss");
  }

  const getUserTZ = (u, fallback = "UTC") => u?.settings?.timezone || fallback;

  const setField = (name, value) =>
    setFormData((s) => (s[name] === value ? s : { ...s, [name]: value }));

  /* ---- API: create appointment ---- */
  const createAppointment = async (leadData, isMultyStep) => {
    setLoading(true);
    try {
      if (!selectedSlotIso) {
        setStatus("error");
        setMessage("Pick a time first.");
        return;
      }
      if (!leadData) {
        setStatus("error");
        setMessage("Lead not loaded yet.");
        return;
      }

      const duration = 45;
      const startUtcIso = selectedSlotIso;
      const endUtcIso = dayjs(startUtcIso).add(duration, "minute").toISOString();

      const proTZ =
        getUserTZ(leadData?.TaxPro?.user) ||
        getUserTZ(leadData?.taxPro?.user) ||
        getUserTZ(taxProAvailability) ||
        "UTC";

      const startDateLocal = convertTimeIntoSselectedTZ("UTC", proTZ, startUtcIso, "YYYY-MM-DD HH:mm:ss");
      const endDateLocal = convertTimeIntoSselectedTZ("UTC", proTZ, endUtcIso, "YYYY-MM-DD HH:mm:ss");

      const appointmentObj = { timezone: proTZ, startDate: startDateLocal, endDate: endDateLocal, duration };

      if (appointmentType === "video") {
        const meetingData = {
          taxProEmail: leadData?.taxPro?.user?.email || leadData?.user?.email,
          email: leadData?.user?.email,
          startTime: moment(selectedSlotIso).tz(proTZ).format("YYYY-MM-DD HH:mm:ss"),
          duration,
          timezone: proTZ,
        };

        await axios
          .post(
            PROXY_URL,
            { path: "/createZoomMeeting", method: "PUT", qa_mode: qa, data: meetingData },
            { headers: { "Content-Type": "application/json" } }
          )
          .then((response) => {
            appointmentObj.roomId = response?.data?.data?.meetingID || null;
          });
      }

      await axios.post(
        PROXY_URL,
        {
          path: `/booking/${leadData?.uuid || leadData?.lead?.uuid}/${leadData?.user?.uuid}/${leadData?.taxPro?.user?.uuid || leadData?.userpro?.user?.uuid}`,
          method: "POST",
          qa_mode: qa,
          data: appointmentObj,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      setLoading(false);

      if (!isMultyStep) {
        if (auto_login) {
          window.location.href = `${CLIENT_URL}/login-with-token/${userCheck?.user?.verification_token}?email=${userCheck?.user?.email}`;
        } else {
          window.location.href = `${CLIENT_URL}/dashboard`;
        }
      }

      setStatus("success");
      setMessage("Appointment created ✅");
    } catch (error) {
      setLoading(false);
      console.log(error);
      setStatus("error");
      setMessage(error?.response?.data?.message || error?.message || "Failed to book appointment.");
    }
  };

  /* ---- API: fetch services ---- */
  useEffect(() => {
    let active = true;
    (async () => {
      setServicesLoading(true);
      try {
        const { data } = await axios.post(
          PROXY_URL,
          { path: "/get-services", method: "GET", qa_mode: qa },
          { headers: { "Content-Type": "application/json" } }
        );
        if (active) setServices(Array.isArray(data) ? data : []);
      } catch {
        if (active) setServices([]);
      } finally {
        if (active) setServicesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [PROXY_URL, qa]);

  /* ---- API: fetch pro schedule ---- */
  const getAvailableTaxPro = async () => {
    setScheduleLoading(true);
    setNoSchedule(false);
    try {
      const resp = await axios.post(
        PROXY_URL,
        { path: "/get-available-taxpro", method: "POST", qa_mode: qa, data: { pro_id } },
        { headers: { "Content-Type": "application/json" } }
      );

      const proUser = resp?.data?.data?.pro?.user;
      const proId = resp?.data?.data?.pro?.id;
      const proUuid = resp?.data?.data?.pro?.uuid;

      if (proUser) {
        setTaxProAvailability({ ...proUser, pro_id: proId, pro_uuid: proUuid });
      } else {
        setNoSchedule(true);
        setTaxProAvailability(null);
      }
    } catch (err) {
      console.log(err);
      setNoSchedule(true);
      setTaxProAvailability(null);
    } finally {
      setScheduleLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(userCheck)?.length > 0 && !userCheck?.taxProAssigned) {
      getAvailableTaxPro();
    }
  }, [userCheck]);

  /* ---- API: fetch user by email ---- */
  const getAvailability = async () => {
    try {
      setStatus("loading");
      const resp = await axios.post(
        PROXY_URL,
        { path: "/getTaxProAvailabilityByEmail", method: "POST", qa_mode: qa, data: { email: emailParam } },
        { headers: { "Content-Type": "application/json" } }
      );

      const maybe =
        resp?.data?.data?.taxPro?.user ||
        resp?.data?.data?.taxPro ||
        resp?.data?.data ||
        resp?.data;

      setUserCheck(resp?.data?.data);
      setLead(resp?.data?.data);

      if (resp?.data?.data?.userExist && resp?.data?.data?.leadExists) {
        if (maybe?.availability?.time_ranges) {
          setTaxProAvailability(maybe || null);
        } else {
          setNoSchedule(true);
          setTaxProAvailability(null);
        }
      }
      setStatus("success");
    } catch (err) {
      console.log("BookAppointmentFormV3 error:", err);
      setStatus("error");
      setMessage(err?.response?.data?.message || err?.message || "Request failed.");
      setTaxProAvailability(null);
    }
  };

  useEffect(() => {
    if (!emailParam) {
      setUserCheck({ userExist: false, leadExists: false });
      setStatus("idle");
      getAvailableTaxPro();
      return;
    }
    getAvailability();
  }, [emailParam, pro_id, qa]);

  const hasPayload =
    !!taxProAvailability &&
    !!taxProAvailability.settings?.timezone &&
    !!taxProAvailability.availability?.time_ranges &&
    typeof taxProAvailability.availability.time_ranges === "object";

  // Protection for today/past selection
  useEffect(() => {
    if (!hasPayload) return;
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const selectedIsPastOrToday =
      new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()) <=
      new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (selectedIsPastOrToday) {
      let found = null;
      for (let i = 0; i < 90; i++) {
        const d = addDays(tomorrow, i);
        const s = buildSlotsForDate(d, taxProAvailability, selectedTZ.value);
        if (s.length > 0) { found = d; break; }
      }
      setSelectedDate(found || tomorrow);
      setSelectedSlotIso(null);
    }
  }, [hasPayload, taxProAvailability, selectedTZ, monthCursor]);

  useEffect(() => {
    if (!hasPayload) return;
    const today = new Date();
    let firstAvailable = null;
    for (let i = 1; i <= 90; i++) {
      const d = addDays(today, i);
      const s = buildSlotsForDate(d, taxProAvailability, selectedTZ.value);
      if (s.length > 0) { firstAvailable = d; break; }
    }
    if (firstAvailable && !isSameMonth(firstAvailable, monthCursor)) {
      setMonthCursor(startOfMonth(firstAvailable));
    }
  }, [hasPayload, selectedTZ, taxProAvailability, monthCursor]);

  // Pro display name
  const proName = taxProAvailability
    ? [taxProAvailability.firstname, taxProAvailability.lastname].filter(Boolean).join(" ") || null
    : null;

  /* ---- Validate step 2 ---- */
  const validateDetails = () => {
    const errs = {};
    if (!formData.firstname.trim()) errs.firstname = true;
    if (!formData.lastname.trim()) errs.lastname = true;
    if (!formData.email.trim() || !formData.email.includes("@")) errs.email = true;
    if (!PHONE_REGEX.test(formData.phone)) errs.phone = true;
    if (!formData.zip.trim() || formData.zip.length < 5) errs.zip = true;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ---- Handle final submit ---- */
  const handleConfirm = async () => {
    setLoading(true);
    setSubmitError("");
    try {
      const isNewUser = !userCheck?.userExist;
      const hasLead = userCheck?.leadExists;
      const hasAssignedPro = userCheck?.taxProAssigned;

      if (isNewUser) {
        // Generate lead for new user
        const leadPayload = {
          ...formData,
          phone: (formData.phone || "").replace(/[ ()-]/g, ""),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        try {
          const ipRes = await axios.get("https://api.ipify.org?format=json");
          leadPayload.clientIp = ipRes?.data?.ip || null;
        } catch { leadPayload.clientIp = null; }

        const res = await axios.post(
          PROXY_URL,
          {
            path: "/generate-lead",
            method: "POST",
            qa_mode: qa,
            data: leadPayload,
            params: { tag: "VSL", pro_id: taxProAvailability?.pro_uuid || null },
          },
          { headers: { "Content-Type": "application/json" } }
        );

        // Fetch the created lead
        const leadResp = await axios.post(
          PROXY_URL,
          {
            path: `/${res?.data?.data?.lead_uuid}/${res?.data?.data?.user_uuid}/${res?.data?.data?.userpro_uuid}`,
            method: "GET",
            qa_mode: qa,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const createdLead = leadResp?.data?.data?.lead;
        await createAppointment(createdLead, true);

        if (auto_login) {
          window.location.href = `${CLIENT_URL}/login-with-token/${res?.data?.data?.user?.verification_token}?email=${res?.data?.data?.user?.email}`;
        } else {
          window.location.href = `${CLIENT_URL}/dashboard`;
        }
      } else if (!hasLead) {
        // Existing user, no lead — create lead then book
        const res = await axios.post(
          PROXY_URL,
          {
            path: "/create-lead-by-email",
            method: "POST",
            qa_mode: qa,
            data: {
              email: emailParam,
              services: formData.options,
              income: formData.income,
              pro_id: taxProAvailability?.pro_id || null,
              zip: formData.zip,
            },
          },
          { headers: { "Content-Type": "application/json" } }
        );

        if (res?.data?.success) {
          await createAppointment(
            {
              lead: res.data.data,
              user: res.data.data?.user,
              taxPro: res.data.data?.userpro,
            },
            false
          );
        }
      } else if (hasAssignedPro) {
        // Existing user with lead and assigned pro — direct booking
        await createAppointment(lead, false);
      }
    } catch (err) {
      console.log(err);
      const msg = err?.response?.data?.message || "Server error. Please try again later.";
      setSubmitError(msg);
    } finally {
      setLoading(false);
    }
  };

  /* ---- Determine which steps to show based on user state ---- */
  const isNewUser = !userCheck?.userExist;
  const hasLead = userCheck?.leadExists;
  const hasAssignedPro = userCheck?.taxProAssigned;
  const ready = userCheck && Object.keys(userCheck).length > 0;

  // For existing user with assigned pro: step 1 → confirm directly
  // For existing user without lead: step 1 → 3 (services) → 4 (income) → confirm
  // For new user: step 1 → 2 (details) → 3 (services) → 4 (income) → confirm
  const needsDetails = isNewUser;
  const needsServices = isNewUser || (userCheck?.userExist && !hasLead);
  const needsIncome = isNewUser || (userCheck?.userExist && !hasLead);

  const goNext = () => {
    if (step === 1) {
      if (hasAssignedPro && hasLead) {
        // Direct booking
        handleConfirm();
        return;
      }
      setStep(needsDetails ? 2 : 3);
    } else if (step === 2) {
      if (validateDetails()) setStep(3);
    } else if (step === 3) {
      setStep(4);
    }
  };

  const goBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(needsDetails ? 2 : 1);
    else if (step === 4) setStep(3);
  };

  return (
    <>
      {!ready && (
        <div className="container py-3">
          {scheduleLoading && (
            <div className="text-center py-5">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Loading...
            </div>
          )}
          {status === "error" && <div className="alert alert-danger py-2">Error: {message}</div>}
        </div>
      )}

      {ready && step === 1 && (
        <StepTimePicker
          hasPayload={hasPayload}
          noSchedule={noSchedule}
          taxProAvailability={taxProAvailability}
          proName={proName}
          tzOptions={tzOptions}
          selectedTZ={selectedTZ}
          setSelectedTZ={(opt) => {
            setSelectedTZ(opt);
            setSelectedSlotIso(null);
          }}
          selectedSlotIso={selectedSlotIso}
          setSelectedSlotIso={setSelectedSlotIso}
          setSelectedDate={setSelectedDate}
          appointmentType={appointmentType}
          setAppointmentType={setAppointmentType}
          loading={loading}
          scheduleLoading={scheduleLoading}
          status={status}
          message={message}
          onContinue={goNext}
        />
      )}

      {ready && step === 2 && (
        <StepDetails
          formData={formData}
          setField={setField}
          errors={fieldErrors}
          onBack={goBack}
          onContinue={goNext}
        />
      )}

      {ready && step === 3 && (
        <StepServices
          services={services}
          servicesLoading={servicesLoading}
          formData={formData}
          setFormData={setFormData}
          onBack={goBack}
          onContinue={goNext}
        />
      )}

      {ready && step === 4 && (
        <StepIncome
          formData={formData}
          setField={setField}
          loading={loading}
          submitError={submitError}
          onBack={goBack}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default BookAppointmentFormV3;
